import { useCallback, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import { Button, Card, CardActions, CardContent, Typography } from '@customink/pigment-react';
import { countryNameByCode, provinceNameByCode } from '~/adapters/geography/countriesAndProvinces';
import { useAddressDefaultToggle } from '~/adapters/services/accounts/postalAddresses/hooks';
import { PostalAddress } from '~/adapters/services/accounts/postalAddresses/types';
import { EditAddressDialog } from './AddressDialog/EditAddressDialog';
import { DeleteDialog } from './DeleteDialog';

export function PostalAddressCard({ address }: { address: PostalAddress }) {
    const theme = useTheme();

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const { mutate } = useAddressDefaultToggle();
    const handleDefaultToggle = useCallback(() => mutate(address), [mutate, address]);

    // country should always be an ISO code, fallback just in case
    const countryFullName = useMemo(
        () => countryNameByCode[address.addressCountry] ?? address.addressCountry,
        [address.addressCountry],
    );
    // region can be either null, an ISO code or a full name
    const regionFullName = useMemo(() => {
        if (!address.addressRegion) {
            return null;
        }
        return provinceNameByCode[address.addressRegion] ?? address.addressRegion;
    }, [address.addressRegion]);

    return (
        <>
            <Card>
                <CardContent sx={{ mt: 0, pt: 2.5 }}>
                    <Typography variant="bodyShort2" paragraph>
                        {address.firstName} {address.lastName}
                    </Typography>
                    <Typography paragraph>
                        <Typography variant="bodyShort1">
                            {address.streetAddress}
                            {address.addressDescription && `, ${address.addressDescription}`}
                        </Typography>
                        <br />
                        {address.addressLocality}, {address.postalCode}
                        <br />
                        {regionFullName && `${regionFullName}, `}
                        {countryFullName} <br />
                        {address.telephone || <>&nbsp;</>}
                    </Typography>
                    <Typography paragraph style={{ color: theme.palette.text.secondary }}>
                        {address.addressType === 'residential' ? 'Residential' : 'Business'}
                        {address.isDefault && <> &bull; Default Address</>}
                    </Typography>
                </CardContent>
                <CardActions style={{ flexDirection: 'row' }}>
                    <Button variant="secondary" onClick={() => setEditDialogOpen(true)}>
                        Edit
                    </Button>
                    <Button variant="link" onClick={() => setDeleteDialogOpen(true)}>
                        Remove
                    </Button>
                    {!address.isDefault && (
                        <Button variant="link" onClick={handleDefaultToggle}>
                            Set As Default
                        </Button>
                    )}
                </CardActions>
            </Card>
            {/* we want to unmount the React Component when closed, so that the form state resets when it's reopened */}
            {editDialogOpen && (
                <EditAddressDialog address={address} open={editDialogOpen} onClose={() => setEditDialogOpen(false)} />
            )}
            <DeleteDialog id={address.id} open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} />
        </>
    );
}
